import { useState, useEffect, useRef } from 'react';
import axios from '../services/api';

/**
 * Shared hook that calls the 6DOF trajectory backend when simulator params change.
 * Debounces API calls to prevent spam on slider drags.
 *
 * @param {Object} simulatedParams - { ball_speed, launch_angle, spin_rate, spin_axis }
 * @returns {Object} { simulation, loading, error }
 */
export function useSimulationResult(simulatedParams) {
  const [simulation, setSimulation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!simulatedParams?.ball_speed || !simulatedParams?.launch_angle || !simulatedParams?.spin_rate) {
      return;
    }

    // Debounce 300ms
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.post('/analyze/simulate/trajectory', {
          ball_speed: simulatedParams.ball_speed,
          launch_angle: simulatedParams.launch_angle,
          spin_rate: simulatedParams.spin_rate,
          spin_axis: simulatedParams.spin_axis || 0,
          altitude_ft: simulatedParams.altitude_ft || 0,
          temp_f: simulatedParams.temp_f || 59,
          humidity_pct: simulatedParams.humidity_pct || 50,
          wind_speed_mph: simulatedParams.wind_speed_mph || 0,
          wind_direction_deg: simulatedParams.wind_direction_deg || 0,
        });

        setSimulation(res.data);
      } catch (err) {
        console.error('Simulation failed:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [
    simulatedParams?.ball_speed,
    simulatedParams?.launch_angle,
    simulatedParams?.spin_rate,
    simulatedParams?.spin_axis,
    simulatedParams?.altitude_ft,
    simulatedParams?.temp_f,
    simulatedParams?.humidity_pct,
    simulatedParams?.wind_speed_mph,
    simulatedParams?.wind_direction_deg,
  ]);

  return { simulation, loading, error };
}
